import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  EditableIcon, EditableMarkdownField, EditableSelect, EditableText, useSectionEditing,
} from '@kevlarcon/pages';
import { PartnerPin } from '../../api/partnerMap';
import { partnersApi, PartnerFilterableField } from '../../api/partners';
import { StateMapData } from '../../api/states';
import StateMap from '../StateMap';

/**
 * What `PartnerMap` (one state's partners) and `PartnerDirectory` (every
 * partner, searchable) share: the section heading, the admin-only config row,
 * the visitor's filter-by-field logic, and the results-list-plus-map body that
 * is the whole point of both. Each section keeps only what differs — where its
 * data and base map come from, its address line and empty-state copy, and (for
 * the directory) a search box.
 *
 * `testIdPrefix` namespaces every `data-testid` the shared parts render
 * (`partner-map-results-count`, `partner-directory-map`, …) so each section
 * keeps its own stable hooks.
 */

/** Eyebrow / heading / lede, each inline-editable while an admin is editing. */
export function PartnerSectionHead({
  eyebrow, heading, lede,
}: { eyebrow?: string; heading: string; lede?: string }) {
  const { active } = useSectionEditing();
  return (
    <div className="head">
      {(eyebrow || active) && (
        <EditableText as="div" className="eyebrow" kind="text" value={eyebrow ?? ''} path={['eyebrow']} />
      )}
      <EditableText as="h2" kind="richtext" value={heading} path={['heading']} />
      {(lede || active) && <EditableMarkdownField value={lede ?? ''} path={['lede']} />}
    </div>
  );
}

/**
 * The marker-icon and filter-field pickers, shown only while an admin is
 * editing the section. The filter picker's options are per-tenant form-schema
 * data (`partnersApi.filterableFields`), so they're fetched only then.
 */
export function PartnerMapConfig({
  testIdPrefix, markerIcon, filterField,
}: { testIdPrefix: string; markerIcon?: string; filterField?: string }) {
  const { active } = useSectionEditing();
  const [filterableFields, setFilterableFields] = useState<PartnerFilterableField[]>([]);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    partnersApi.getForm()
      .then((form) => (form ? partnersApi.filterableFields(form.id) : []))
      .then((fields) => { if (!cancelled) setFilterableFields(fields); });
    return () => { cancelled = true; };
  }, [active]);

  if (!active) return null;
  return (
    <div className="partner-map-config" data-testid={`${testIdPrefix}-config`}>
      <label>
        Marker icon
        <EditableIcon value={markerIcon ?? ''} path={['markerIcon']} size={20} />
      </label>
      <label>
        Filter partners by
        <EditableSelect
          value={filterField ?? ''}
          path={['filterField']}
          options={filterableFields.map((f) => f.name)}
          title="Filter partners by field"
          renderTrigger={(name) => filterableFields.find((f) => f.name === name)?.label || 'None'}
          renderOption={(name) => filterableFields.find((f) => f.name === name)?.label ?? name}
        />
      </label>
    </div>
  );
}

/**
 * The visitor-facing filter over the chosen `filterField`: the distinct values
 * across `pins`, the one currently picked (reset whenever `resetKey` changes),
 * and `pins` narrowed to it.
 */
export function usePartnerFilter(pins: PartnerPin[], resetKey: unknown) {
  const [activeFilter, setActiveFilter] = useState('');
  useEffect(() => setActiveFilter(''), [resetKey]);

  const filterValues = useMemo(
    () => Array.from(new Set(
      pins.map((pin) => pin.filter_value).filter((v): v is string => Boolean(v)),
    )).sort(),
    [pins],
  );
  const filtered = useMemo(
    () => (activeFilter ? pins.filter((pin) => pin.filter_value === activeFilter) : pins),
    [pins, activeFilter],
  );
  return { filterValues, activeFilter, setActiveFilter, filtered };
}

type Props = {
  testIdPrefix: string;
  /** The pins to list and draw, already filtered and sorted. */
  pins: PartnerPin[];
  /** True once the partner data has loaded (the count stays blank until then). */
  loaded: boolean;
  /** Shown in the list's place when `pins` is empty — loading, none, or error. */
  emptyMessage: string;
  /** The base map to draw the pins on; undefined shows `mapPlaceholder`. */
  mapData?: StateMapData;
  mapPlaceholder: string;
  mapAriaLabel: string;
  markerIcon?: string;
  /** The second line of each result (street + city, city + state, …). */
  renderAddress: (pin: PartnerPin) => string;
  /** Controls rendered before / after the result count in the list's header. */
  controlsBefore?: ReactNode;
  controlsAfter?: ReactNode;
};

/**
 * A fixed-height results list down the left quarter, scrolling on its own so a
 * long list never pushes the map off screen, and the map with a pin per partner
 * across the other three. Hovering either side highlights the same partner on
 * the other; clicking either opens the partner's page.
 */
export default function PartnerResultsMap({
  testIdPrefix, pins, loaded, emptyMessage, mapData, mapPlaceholder, mapAriaLabel,
  markerIcon, renderAddress, controlsBefore, controlsAfter,
}: Props) {
  const { background } = useSectionEditing();
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState<number | null>(null);
  const listRef = useRef<HTMLOListElement>(null);

  // Hovering a pin scrolls its result into view *within the list* —
  // `scrollIntoView` would scroll the page too, yanking the map away.
  const hoverPin = (pin: PartnerPin | null) => {
    setActiveId(pin?.id ?? null);
    const list = listRef.current;
    const item = pin && list?.querySelector<HTMLElement>(`[data-partner-id="${pin.id}"]`);
    if (!list || !item) return;
    const top = item.offsetTop; // the list is position: relative, so already list-relative
    if (top < list.scrollTop || top + item.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTo({ top: top - (list.clientHeight - item.offsetHeight) / 2, behavior: 'smooth' });
    }
  };

  const count = pins.length;

  return (
    <div className="partner-map-layout">
      <aside className="partner-map-results" aria-label="Partner schools">
        <div className="partner-map-results-head">
          {controlsBefore}
          <span className="partner-map-results-count" data-testid={`${testIdPrefix}-results-count`}>
            {loaded ? `${count} ${count === 1 ? 'school' : 'schools'}` : ' '}
          </span>
          {controlsAfter}
        </div>

        {loaded && count > 0 ? (
          <ol className="partner-map-results-list" ref={listRef}>
            {pins.map((pin) => (
              <li key={pin.id} data-partner-id={pin.id}>
                <Link
                  to={pin.url}
                  className={`partner-map-result${pin.id === activeId ? ' is-active' : ''}`}
                  data-testid={`${testIdPrefix}-result-${pin.id}-link`}
                  onMouseEnter={() => setActiveId(pin.id)}
                  onMouseLeave={() => setActiveId(null)}
                  onFocus={() => setActiveId(pin.id)}
                  onBlur={() => setActiveId(null)}
                >
                  <span className="partner-map-result-name">{pin.school_name}</span>
                  {renderAddress(pin) && (
                    <span className="partner-map-result-address">{renderAddress(pin)}</span>
                  )}
                  {pin.filter_value && (
                    <span className="partner-map-result-tag">{pin.filter_value}</span>
                  )}
                </Link>
              </li>
            ))}
          </ol>
        ) : (
          <div className="partner-map-results-empty" data-testid={`${testIdPrefix}-empty`}>
            {emptyMessage}
          </div>
        )}
      </aside>

      <div className="partner-map-stage">
        {mapData ? (
          <StateMap
            data={mapData}
            padding={40}
            className="state-map-canvas partner-map-canvas"
            testId={`${testIdPrefix}-map`}
            ariaLabel={mapAriaLabel}
            partners={pins}
            activePartnerId={activeId}
            onPartnerHover={hoverPin}
            markerIcon={markerIcon}
            onPartnerSelect={(pin: PartnerPin) => navigate(pin.url)}
            background={background}
          />
        ) : (
          <div
            className="state-map-canvas partner-map-canvas state-map-placeholder"
            data-testid={`${testIdPrefix}-map-placeholder`}
          >
            <span>{mapPlaceholder}</span>
          </div>
        )}
      </div>
    </div>
  );
}
